import { Popover } from "@base-ui/react/popover";
import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import type React from "react";
import {
  type Dispatch,
  forwardRef,
  type SetStateAction,
  useEffect,
  useState,
} from "react";
import {
  tooltipPopupClass,
  tooltipPositionerClass,
} from "../Tooltip/styles.css";
import { dropdownContainerClass, dropdownPositionerClass } from "./styles.css";

export type DropdownSide =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "inline-end"
  | "inline-start";
export type DropdownAlign = "start" | "center" | "end";

interface DropdownProps {
  anchor: string;
  isOpen?: boolean;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
  children?: React.ReactNode;
  side?: DropdownSide;
  align?: DropdownAlign;
  sideOffset?: number;
  tooltip?: string;
}

export const Dropdown = forwardRef(function Dropdown(
  {
    anchor,
    isOpen,
    setIsOpen,
    children,
    side = "bottom",
    align = "end",
    sideOffset = 4,
    tooltip,
  }: DropdownProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorWidth, setAnchorWidth] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const open = isOpen !== undefined ? isOpen : internalOpen;

  useEffect(() => {
    const el = document.getElementById(anchor);
    setAnchorEl(el);
    if (el) {
      setAnchorWidth(el.getBoundingClientRect().width);
    }

    const toggleDropdown = () => {
      if (setIsOpen) {
        setIsOpen((state) => !state);
      } else {
        setInternalOpen((state) => !state);
      }
    };

    const showTooltip = () => setTooltipOpen(true);
    const hideTooltip = () => setTooltipOpen(false);

    if (el) {
      el.addEventListener("click", toggleDropdown);
      if (tooltip) {
        el.addEventListener("mouseenter", showTooltip);
        el.addEventListener("mouseleave", hideTooltip);
      }
    }

    return () => {
      if (el) {
        el.removeEventListener("click", toggleDropdown);
        el.removeEventListener("mouseenter", showTooltip);
        el.removeEventListener("mouseleave", hideTooltip);
      }
    };
  }, [anchor, tooltip, setIsOpen]);

  const handleOpenChange = (
    nextOpen: boolean,
    eventDetails: Popover.Root.ChangeEventDetails,
  ) => {
    if (!nextOpen && eventDetails.reason === "outside-press" && anchorEl) {
      const event = eventDetails.event as MouseEvent;
      if (anchorEl.contains(event.target as Node)) {
        // Anchor click — our listener handles the toggle
        return;
      }
    }
    setInternalOpen(nextOpen);
    setIsOpen?.(nextOpen);
  };

  return (
    <>
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        <Popover.Portal>
          <Popover.Positioner
            anchor={anchorEl}
            side={side}
            align={align}
            sideOffset={sideOffset}
            className={dropdownPositionerClass}
          >
            <Popover.Popup
              ref={ref}
              initialFocus={false}
              finalFocus={false}
              className={dropdownContainerClass}
              style={{ minWidth: anchorWidth || "0" }}
            >
              {children}
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>

      {tooltip && (
        <BaseTooltip.Provider>
          <BaseTooltip.Root open={tooltipOpen}>
            <BaseTooltip.Portal>
              <BaseTooltip.Positioner
                anchor={anchorEl}
                side="top"
                sideOffset={4}
                className={tooltipPositionerClass}
              >
                <BaseTooltip.Popup className={tooltipPopupClass}>
                  {tooltip}
                </BaseTooltip.Popup>
              </BaseTooltip.Positioner>
            </BaseTooltip.Portal>
          </BaseTooltip.Root>
        </BaseTooltip.Provider>
      )}
    </>
  );
});

Dropdown.displayName = "Dropdown";
